import { forwardRef, type ReactElement } from 'react';
import { NavLink } from 'react-router-dom';

// Grouped admin nav (ADR 0076). The console is organized by what you DO with a
// thing, not by feature name:
//
//   MANAGE — the shared fleet objects/workspaces we act on (people, the
//            device-model catalog, the manuals workspace).
//   USAGE  — read-only cost/observability (the two spend sources of the manual
//            crawl: LLM calls and web search).
//
// GROWTH RULES — encoded here so adding the next section is a zero-argument move
// and we never reshuffle the tree again:
//   R1. MANAGE = fleet objects/workspaces we act on. Boats/Components are
//       catalog peers of Devices and slot in here.
//   R2. A future cross-fleet workflow QUEUE (e.g. a moderation queue) is a
//       sibling of Manuals in MANAGE — same "fleet-wide workspace" shape.
//   R3. USAGE = read-only cost/observability. LLM + Web search are the two
//       spend sources of the same feature; co-located so total AI spend is one
//       hop.
//   R4. A future OPERATIONAL group (config/health/audit) is introduced only
//       when a SECOND operational item exists. Until then Audit log ships as a
//       commented USAGE sibling.
//   R5. A group header earns its place at 2+ leaves. A lone reserved item never
//       gets its own one-item header (the inverse of a junk-drawer "Other").
//   R6. EVALS = how good the assistants are: what is deployed and whether it
//       is evaluated, every stored eval run, and (next) the case inbox and judge
//       labels. Evals span Neo chat and the Watch, so they outgrew the Neo
//       group's reserved "Eval cases" slot. Run detail and Compare are reached
//       from a run, not the sidebar. Its Guide (TOM-139) explains the group and
//       how to improve Neo and its experts with the console.
//
// Reserved (commented) entries document the intended growth path under these
// rules.
interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

// Lucide glyphs (lucide.dev) for the sidebar rows, drawn in the accent colour
// like an iPadOS sidebar. Decorative: each link's name is its label.
const NAV_ICONS: Record<string, ReactElement> = {
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  sailboat: (
    <>
      <path d="M22 18H2a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4Z" />
      <path d="M21 14 10 2 3 14h18Z" />
      <path d="M10 2v16" />
    </>
  ),
  cpu: (
    <>
      <rect width="16" height="16" x="4" y="4" rx="2" />
      <rect width="6" height="6" x="9" y="9" rx="1" />
      <path d="M15 2v2M15 20v2M2 15h2M2 9h2M20 15h2M20 9h2M9 2v2M9 20v2" />
    </>
  ),
  book: (
    <>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </>
  ),
  fileX: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="m14.5 12.5-5 5M9.5 12.5l5 5" />
    </>
  ),
  chat: <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
  scale: (
    <>
      <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="M7 21h10M12 3v18M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
    </>
  ),
  shield: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="M12 8v4M12 16h.01" />
    </>
  ),
  pull: (
    <>
      <circle cx="18" cy="18" r="3" />
      <circle cx="6" cy="6" r="3" />
      <path d="M13 6h3a2 2 0 0 1 2 2v7M6 9v12" />
    </>
  ),
  gauge: (
    <>
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </>
  ),
  list: <path d="M3 6h.01M3 12h.01M3 18h.01M8 6h13M8 12h13M8 18h13" />,
  inbox: (
    <>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  tag: (
    <>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r="1" />
    </>
  ),
  coins: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
      <path d="M7 6h1v4" />
      <path d="m16.71 13.88.7.71-2.82 2.82" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  ban: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m4.9 4.9 14.2 14.2" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14a9 3 0 0 0 18 0V5" />
      <path d="M3 12a9 3 0 0 0 18 0" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </>
  ),
  palette: (
    <>
      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </>
  ),
  sparkles: (
    <>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4M22 5h-4M4 17v2M5 18H3" />
    </>
  ),
};

function NavIcon({ name }: { name: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {NAV_ICONS[name]}
    </svg>
  );
}

const GROUPS: Array<{ header: string; items: NavItem[] }> = [
  {
    header: 'Manage',
    items: [
      { to: '/admin', label: 'Users', icon: 'users', end: true },
      { to: '/admin/boats', label: 'Boats', icon: 'sailboat' },
      { to: '/admin/devices', label: 'Devices', icon: 'cpu' },
      { to: '/admin/manuals', label: 'Manuals', icon: 'book' },
      // R1 — the models Neomaris uses, chosen and stored here (TOM-465, ADR 0322).
      { to: '/admin/models', label: 'Models', icon: 'sparkles' },
      // The beta waitlist (TOM-634, ADR 0384).
      { to: '/admin/beta-signups', label: 'Beta signups', icon: 'list' },
      // The public status page's incidents (TOM-930).
      { to: '/admin/status', label: 'Status page', icon: 'gauge' },
      // The design catalog: the chart library and every board panel (ADR 0131,
      // TOM-673). This is its only link; neither the app nor iOS shows one.
      { to: '/design/charts', label: 'Design catalog', icon: 'palette' },
      // R1/R2 — future MANAGE peers:
      // { to: '/admin/components', label: 'Components' },
    ],
  },
  {
    // NEO — the act-on-it quality workspace for the Neo assistant (ADR 0119).
    // Distinct from USAGE (read-only spend): here an operator triages failing
    // chats and turns them into eval cases. Two leaves earn the header (R5).
    // Eval cases moved to the Evals group below (R6).
    header: 'Neo',
    items: [
      { to: '/admin/neo', label: 'Chats', icon: 'chat', end: true },
      { to: '/admin/neo/refusals', label: 'Refusals', icon: 'shield' },
      // Board page saves the page check refused (TOM-672, ADR 0395).
      { to: '/admin/neo/page-refusals', label: 'Page refusals', icon: 'fileX' },
      // Where the input gate's classifier and the regex disagreed (ADR 0300).
      { to: '/admin/neo/gate', label: 'Gate', icon: 'scale' },
      // Product changes an admin asks Neo for (ADR 0231).
      { to: '/admin/neo/changes', label: 'Claude', icon: 'pull' },
    ],
  },
  {
    // EVALS (R6; task eval_admin_runs). Two leaves earn the header (R5).
    header: 'Evals',
    items: [
      { to: '/admin/evals', label: 'Overview', icon: 'gauge', end: true },
      { to: '/admin/evals/runs', label: 'Runs', icon: 'list' },
      // R6 — next, from tasks eval_case_inbox and eval_judge_labels:
      { to: '/admin/evals/cases', label: 'Cases', icon: 'inbox' },
      { to: '/admin/evals/labels', label: 'Labels', icon: 'tag' }, // judge calibration (TOM-141)
      // How the section works and how to improve Neo and its experts (TOM-139).
      // Every Evals page links to its part of it.
      { to: '/admin/evals/guide', label: 'Guide', icon: 'help' },
    ],
  },
  {
    header: 'Usage',
    items: [
      { to: '/admin/usage/llm', label: 'LLM spend', icon: 'coins' },
      { to: '/admin/usage/search', label: 'Web search', icon: 'search' },
      // R3 — read-only observability: telemetry paths boats send that the
      // ingest policy rejects (the schema evolution signal, ADR 0149).
      { to: '/admin/usage/rejected-telemetry', label: 'Rejected telemetry', icon: 'ban' },
      // R3 — read-only observability: per boat, per day ingest counters and the
      // nightly storage sizes (ADR 0209).
      { to: '/admin/usage/ingest', label: 'Ingest and storage', icon: 'database' },
      // R4/R5 — Audit log lives here until a second operational item exists,
      // at which point an Operational group (config/health/audit) is split out:
      // { to: '/admin/audit', label: 'Audit log' },
    ],
  },
];

export const AdminSidebar = forwardRef<HTMLElement>(function AdminSidebar(_, ref) {
  return (
    <nav ref={ref} className="admin-sidebar" id="admin-sidebar" aria-label="Admin sections">
      <ul>
        {GROUPS.map((group) => (
          <li key={group.header} className="admin-nav-group">
            <span className="admin-nav-group-header">{group.header}</span>
            <ul>
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end ?? false}>
                    <NavIcon name={item.icon} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
});
